import React, { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { Truck, IndianRupee, ArrowRight, CalendarOff } from 'lucide-react';
import { apiGet } from '../../lib/api';
import { toast } from 'sonner';

export default function CustomerHome() {
  const [data, setData] = useState(null);
  const { selectedCustomerId, loadingProviders } = useOutletContext() || {};

  useEffect(() => {
    if (loadingProviders) return;
    const query = selectedCustomerId ? `?customer_id=${selectedCustomerId}` : '';
    apiGet(`/customer/dashboard${query}`).then(r => setData(r.data)).catch(e => toast.error(e.message));
  }, [selectedCustomerId, loadingProviders]);

  if (loadingProviders || !data) return <div className="text-rozops-text-muted">Loading…</div>;

  if (!data.linked) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-6 text-center">
        <div className="font-heading font-bold text-lg text-rozops-navy">Link with your service provider</div>
        <p className="text-sm text-rozops-text-muted mt-1">Your account isn't linked yet.</p>
        <Link to="/onboarding" className="mt-4 inline-block bg-rozops-blue text-white rounded-xl px-5 py-2.5 font-semibold">Link account</Link>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="customer-home">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-rozops-blue-light text-rozops-blue grid place-items-center"><Truck className="w-6 h-6" /></div>
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-rozops-text-muted">Today's delivery</div>
          {data.today_delivery?.length > 0 ? data.today_delivery.map(d => (
            <div key={d.id} className="font-semibold text-rozops-text">
              {d.provider?.business?.business_name ? <span className="text-xs text-rozops-text-muted">{d.provider.business.business_name} · </span> : null}
              {d.product?.name}: {d.delivered_quantity || d.planned_quantity} {d.product?.unit} · <span className={`text-xs ${d.status === 'delivered' ? 'text-rozops-green' : 'text-amber-600'}`}>{d.status}</span>
            </div>
          )) : <div className="text-sm text-rozops-text-muted">No delivery today.</div>}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5">
        <div className="text-xs uppercase tracking-widest font-bold text-rozops-text-muted mb-3">Active subscriptions</div>
        {(data.active_subscriptions || []).length === 0 && <div className="text-sm text-rozops-text-muted">No active subscriptions yet.</div>}
        {(data.active_subscriptions || []).map(s => (
          <div key={s.id} className="flex justify-between items-center py-2 border-b border-gray-50 last:border-0">
            <div>
              <div className="font-semibold text-rozops-text">{s.product?.name}</div>
              {s.provider?.business?.business_name && <div className="text-xs text-rozops-text-muted">{s.provider.business.business_name}</div>}
              <div className="text-xs text-rozops-text-muted">{s.quantity} {s.product?.unit} · {s.frequency}</div>
            </div>
            <Link to="/my/subscriptions" className="text-rozops-blue text-sm font-semibold inline-flex items-center gap-1">Manage <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
        ))}
      </div>

      <div className="bg-rozops-navy text-white rounded-2xl p-5 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-white/60 font-bold">Amount due</div>
          <div className="font-heading font-extrabold text-3xl mt-1">₹{Number(data.total_due || 0).toFixed(2)}</div>
          <div className="text-xs text-white/60 mt-1">{(data.outstanding_invoices || []).length} pending bill(s)</div>
        </div>
        <Link to="/my/bills" className="bg-white text-rozops-navy rounded-xl px-4 py-2.5 font-semibold inline-flex items-center gap-2"><IndianRupee className="w-4 h-4" /> View bills</Link>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Link to="/my/vacation" className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5" data-testid="quick-vacation">
          <CalendarOff className="w-5 h-5 text-rozops-blue mb-2" />
          <div className="font-heading font-bold text-rozops-navy">Vacation</div>
          <div className="text-xs text-rozops-text-muted mt-1">Pause deliveries</div>
        </Link>
        <Link to="/my/subscriptions" className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5" data-testid="quick-qty">
          <ArrowRight className="w-5 h-5 text-rozops-green mb-2" />
          <div className="font-heading font-bold text-rozops-navy">Change qty</div>
          <div className="text-xs text-rozops-text-muted mt-1">Request update</div>
        </Link>
      </div>
    </div>
  );
}
